import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Alert,
  RefreshControl,
  ActivityIndicator,
  Linking,
  useWindowDimensions
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import RenderHtml from 'react-native-render-html';
import notificationApi from '../services/notificationApi';
import SharedHeader from '../components/SharedHeader';

const NotificationScreen = ({ navigation }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [summary, setSummary] = useState({ total_notifications: 0, unread_count: 0, read_count: 0 });
  const { width } = useWindowDimensions();

  // Load notifications
  const loadNotifications = useCallback(async (pageNum = 1, filterStatus = filter, reset = false) => {
    try {
      if (pageNum === 1) setLoading(true);
      
      const response = await notificationApi.getNotifications(pageNum, 10, filterStatus);
      
      if (response.status === 'success') {
        const newNotifications = response.data.notifications;
        
        if (reset || pageNum === 1) {
          setNotifications(newNotifications);
        } else {
          setNotifications(prev => [...prev, ...newNotifications]);
        }
        
        setHasMore(response.data.pagination.has_next);
        setSummary(response.data.summary);
        setPage(pageNum);
      } else {
        Alert.alert('Error', response.message || 'Failed to load notifications');
      }
    } catch (error) {
      console.error('Load notifications error:', error);
      Alert.alert('Error', 'Failed to load notifications. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filter]);

  // Initial load
  useEffect(() => {
    loadNotifications(1, filter, true);
  }, [filter]);

  // Refresh notifications
  const onRefresh = useCallback(() => {
    setRefreshing(true);
    loadNotifications(1, filter, true);
  }, [filter, loadNotifications]);

  // Load more notifications
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadNotifications(page + 1, filter, false);
    }
  }, [loading, hasMore, page, filter, loadNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const response = await notificationApi.markAsRead(notificationId);
      if (response.status === 'success') {
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, is_read: true }
              : notif
          )
        );
        setSummary(prev => ({
          ...prev,
          unread_count: Math.max(0, prev.unread_count - 1),
          read_count: prev.read_count + 1
        }));
      }
    } catch (error) {
      console.error('Mark as read error:', error);
      Alert.alert('Error', 'Failed to mark notification as read');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const response = await notificationApi.markAllAsRead();
      if (response.status === 'success') {
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, is_read: true }))
        );
        setSummary(prev => ({
          ...prev,
          unread_count: 0,
          read_count: prev.total_notifications
        }));
        Alert.alert('Success', 'All notifications marked as read');
      }
    } catch (error) {
      console.error('Mark all as read error:', error);
      Alert.alert('Error', 'Failed to mark all notifications as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    Alert.alert(
      'Delete Notification',
      'Are you sure you want to delete this notification?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const response = await notificationApi.deleteNotification(notificationId);
              if (response.status === 'success') {
                setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
                setSummary(prev => ({
                  ...prev,
                  total_notifications: prev.total_notifications - 1
                }));
              }
            } catch (error) {
              console.error('Delete notification error:', error);
              Alert.alert('Error', 'Failed to delete notification');
            }
          }
        }
      ]
    );
  };

  // Handle notification link
  const handleNotificationLink = async (notification) => {
    // Mark as read if not already read
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Handle link navigation
    if (notification.link) {
      const link = String(notification.link);
      const isPointExpiry =
        link.includes('point/expiry') ||
        link.includes('point-expiry') ||
        link.includes('PointExpiry');

      if (isPointExpiry) {
        navigation.navigate('PointExpiry');
        return;
      }

      if (link.startsWith('http')) {
        Linking.openURL(link);
      } else {
        console.log('Navigate to:', link);
      }
    }
  };

  // Render notification item
  const renderNotificationItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.notificationItem,
        !item.is_read && styles.unreadNotification
      ]}
      onPress={() => handleNotificationLink(item)}
    >
      <View style={styles.notificationContent}>
        <View style={styles.notificationHeader}>
          <View style={styles.notificationIndicator}>
            {!item.is_read && <View style={styles.unreadDot} />}
          </View>
          <Text style={styles.notificationDate}>
            {notificationApi.formatDate(item.created_at)}
          </Text>
        </View>
        
        <RenderHtml
          contentWidth={width - 64}
          source={{ html: item.message }}
          tagsStyles={{
            p: {
              fontSize: 14,
              color: !item.is_read ? '#000' : '#333',
              fontWeight: !item.is_read ? '600' : 'normal',
              lineHeight: 20,
              marginBottom: 4,
              marginTop: 0
            },
            div: {
              fontSize: 14,
              color: !item.is_read ? '#000' : '#333',
              fontWeight: !item.is_read ? '600' : 'normal',
              lineHeight: 20,
              marginBottom: 4,
              marginTop: 0
            },
            span: {
              fontSize: 14,
              color: !item.is_read ? '#000' : '#333',
              fontWeight: !item.is_read ? '600' : 'normal'
            },
            strong: {
              fontWeight: 'bold'
            },
            b: {
              fontWeight: 'bold'
            },
            em: {
              fontStyle: 'italic'
            },
            i: {
              fontStyle: 'italic'
            },
            a: {
              color: '#FF6B35',
              textDecorationLine: 'underline'
            }
          }}
          systemFonts={['System']}
        />
        
        {item.link && (
          <Text style={styles.notificationLink}>Tap to view details</Text>
        )}
      </View>
      
      <View style={styles.notificationActions}>
        {!item.is_read && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => markAsRead(item.id)}
          >
            <Ionicons name="checkmark-circle-outline" size={20} color="#4CAF50" />
          </TouchableOpacity>
        )}
        
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => deleteNotification(item.id)}
        >
          <Ionicons name="trash-outline" size={20} color="#F44336" />
        </TouchableOpacity>
      </View>
    </TouchableOpacity>
  );

  // Render filter buttons
  const renderFilterButtons = () => (
    <View style={styles.filterContainer}>
      {['all', 'unread', 'read'].map((filterType) => (
        <TouchableOpacity
          key={filterType}
          style={[
            styles.filterButton,
            filter === filterType && styles.activeFilterButton
          ]}
          onPress={() => setFilter(filterType)}
        >
          <Text style={[
            styles.filterButtonText,
            filter === filterType && styles.activeFilterButtonText
          ]}>
            {filterType === 'all' ? 'All' : filterType === 'unread' ? 'Unread' : 'Read'}
            {filterType === 'all' && ` (${summary.total_notifications})`}
            {filterType === 'unread' && ` (${summary.unread_count})`}
            {filterType === 'read' && ` (${summary.read_count})`}
          </Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  // Render header actions
  const renderHeaderActions = () => (
    <View style={styles.headerActions}>
      {summary.unread_count > 0 && (
        <TouchableOpacity
          style={styles.markAllButton}
          onPress={markAllAsRead}
        >
          <Text style={styles.markAllButtonText}>Mark All Read</Text>
        </TouchableOpacity>
      )}
    </View>
  );

  return (
    <View style={styles.container}>
      <SharedHeader 
        title="Notifications" 
        onBackPress={() => navigation.goBack()}
        onNotificationPress={() => navigation.navigate('Notification')}
        showNotificationIcon={false}
      />
      
      {renderFilterButtons()}
      {renderHeaderActions()}
      
      {loading && notifications.length === 0 ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FF6B35" />
          <Text style={styles.loadingText}>Loading notifications...</Text>
        </View>
      ) : (
        <FlatList
          data={notifications}
          renderItem={renderNotificationItem}
          keyExtractor={(item) => item.id.toString()}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={['#FF6B35']}
            />
          }
          onEndReached={loadMore}
          onEndReachedThreshold={0.1}
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="notifications-outline" size={64} color="#ccc" />
              <Text style={styles.emptyText}>No notifications found</Text>
              <Text style={styles.emptySubText}>
                {filter === 'unread' ? 'You have no unread notifications' : 
                 filter === 'read' ? 'You have no read notifications' : 
                 'You have no notifications yet'}
              </Text>
            </View>
          }
          ListFooterComponent={
            loading && notifications.length > 0 ? (
              <View style={styles.footerLoading}>
                <ActivityIndicator size="small" color="#FF6B35" />
              </View>
            ) : null
          }
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5'
  },
  filterContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0'
  },
  filterButton: {
    flex: 1,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginHorizontal: 4,
    borderRadius: 20,
    backgroundColor: '#f0f0f0',
    alignItems: 'center'
  },
  activeFilterButton: {
    backgroundColor: '#FF6B35'
  },
  filterButtonText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#666'
  },
  activeFilterButtonText: {
    color: '#fff'
  },
  headerActions: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0'
  },
  markAllButton: {
    alignSelf: 'flex-end',
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#4CAF50',
    borderRadius: 16
  },
  markAllButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500'
  },
  notificationItem: {
    backgroundColor: '#fff',
    marginHorizontal: 16,
    marginVertical: 4,
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2
  },
  unreadNotification: {
    borderLeftWidth: 4,
    borderLeftColor: '#FF6B35'
  },
  notificationContent: {
    flex: 1
  },
  notificationHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8
  },
  notificationIndicator: {
    width: 12,
    height: 12,
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center'
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FF6B35'
  },
  notificationDate: {
    fontSize: 12,
    color: '#999',
    flex: 1
  },
  notificationMessage: {
    fontSize: 14,
    color: '#333',
    lineHeight: 20,
    marginBottom: 4
  },
  unreadMessage: {
    fontWeight: '600',
    color: '#000'
  },
  notificationLink: {
    fontSize: 12,
    color: '#FF6B35',
    fontStyle: 'italic'
  },
  notificationActions: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 12
  },
  actionButton: {
    padding: 8,
    marginLeft: 4
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center'
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#666'
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 64
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#666',
    marginTop: 16
  },
  emptySubText: {
    fontSize: 14,
    color: '#999',
    marginTop: 8,
    textAlign: 'center'
  },
  footerLoading: {
    paddingVertical: 16,
    alignItems: 'center'
  }
});

export default NotificationScreen;